import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CTAButton from './CTAButton';
import StackedTitle from './StackedTitle';
import useGTM from '../hooks/useGTM';
import { waitlistService } from '../services/api';
import { pushToDataLayer } from '../config/gtm';

// Helper para pushToDataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// ---------------------------------------------
// Countries (value = ISO2) + helpers GA4
// ---------------------------------------------
type CountryOpt = { value: string; label: string };

const COUNTRIES: CountryOpt[] = [
  // Hispanic America
  { value: 'AR', label: 'Argentina' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CU', label: 'Cuba' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'HN', label: 'Honduras' },
  { value: 'MX', label: 'Mexico' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'PA', label: 'Panama' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'PE', label: 'Peru' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'ES', label: 'Spain' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'VE', label: 'Venezuela' },
  // Key markets
  { value: 'US', label: 'United States' },
  { value: 'BR', label: 'Brazil' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  // Fallback
  { value: 'OTHER', label: 'Other' },
];

const getCountryLabel = (iso2: string) =>
  COUNTRIES.find((c) => c.value === iso2)?.label || 'Other';

const stripDiacritics = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const gaNormalizeCountry = (input: string) => {
  // Accepts ISO2 or name; returns { iso2, name }
  if (!input) return { iso2: 'OTHER', name: 'Other' };

  const upper = input.toUpperCase().trim();

  // If already valid ISO2 in our list
  if (COUNTRIES.some((c) => c.value === upper)) {
    return { iso2: upper, name: getCountryLabel(upper) };
  }

  // Normalize name and map common cases
  const s = stripDiacritics(input).toLowerCase().trim();

  const map: Record<string, string> = {
    'ecuador': 'EC',
    'spain': 'ES',
    'espana': 'ES',
    'mexico': 'MX',
    'peru': 'PE',
    'united states': 'US',
    'usa': 'US',
    'united kingdom': 'GB',
    'uk': 'GB',
    'england': 'GB',
    'brazil': 'BR',
    'canada': 'CA',
    'argentina': 'AR',
    'chile': 'CL',
    'colombia': 'CO',
    'uruguay': 'UY',
    'venezuela': 'VE',
    'bolivia': 'BO',
    'paraguay': 'PY',
    'panama': 'PA',
    'guatemala': 'GT',
    'honduras': 'HN',
    'nicaragua': 'NI',
    'el salvador': 'SV',
    'costa rica': 'CR',
    'dominican republic': 'DO',
    'cuba': 'CU',
    'puerto rico': 'PR',
    'france': 'FR',
    'germany': 'DE',
    'italy': 'IT',
  };

  const iso2 = map[s] || 'OTHER';
  return { iso2, name: getCountryLabel(iso2) };
};

// ---------------------------------------------
// Types and constants
// ---------------------------------------------
const ReservarPageEN: React.FC = () => {
  const navigate = useNavigate();
  const { trackCTA, trackVideo, trackScroll, trackAction } = useGTM();
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);
  const [scrollDepth, setScrollDepth] = useState<Set<number>>(new Set());
  
  // Hero video milestones
  const [heroVideoMilestones, setHeroVideoMilestones] = useState<Set<number>>(new Set());
  
  // Kickstarter form
  const [kickstarterEmail, setKickstarterEmail] = useState<string>('');
  const [kickstarterPrivacy, setKickstarterPrivacy] = useState<boolean>(false);
  const [kickstarterSuccess, setKickstarterSuccess] = useState<boolean>(false);
  const [kickstarterLoading, setKickstarterLoading] = useState<boolean>(false);
  const [kickstarterError, setKickstarterError] = useState<string | null>(null);
  
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  // Tracking scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
      
      [25, 50, 75, 100].forEach((m) => {
        if (scrollPercentage >= m && !scrollDepth.has(m)) {
          setScrollDepth((prev) => {
            const newSet = new Set(prev);
            newSet.add(m);
            return newSet;
          });
          trackScroll(m);
          pushToDataLayer({ event: 'scroll_depth', scroll_depth: m, page_path: '/landingpage2EN' });
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollDepth, trackScroll]);

  // Configure language and page variables in GTM so only English pixel fires
  useEffect(() => {
    // Send variables to dataLayer to identify this is the English reservation page
    pushToDataLayer({
      page_language: 'en',
      page_type: 'reservar_en',
      page_path: '/landingpage2EN',
      pixel_language: 'en' // Specific variable to control which pixel to fire
    });
  }, []);

  // view_hero
  useEffect(() => {
    trackAction('view_hero', 'hero', 'reservar_page');
    pushToDataLayer({ event: 'view_hero', page_path: '/landingpage2EN' });
  }, [trackAction]);

  // Force transparent background on body when component mounts
  useEffect(() => {
    const originalBodyBg = document.body.style.backgroundColor;
    const originalHtmlBg = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    
    return () => {
      document.body.style.backgroundColor = originalBodyBg;
      document.documentElement.style.backgroundColor = originalHtmlBg;
    };
  }, []);

  // Video helpers
  const handleVideoPlay = () => {
    trackAction('video_start', 'video', 'hero_video');
    trackVideo('play', 'Video 8 books', 'Hero Section');
  };

  const handleHeroVideoProgress = () => {
    if (heroVideoRef.current && heroVideoRef.current.duration > 0) {
      const p = Math.round((heroVideoRef.current.currentTime / heroVideoRef.current.duration) * 100);
      const milestones: number[] = [25, 50, 75, 100];
      const next = milestones.find((m) => p >= m && !heroVideoMilestones.has(m));

      if (next) {
        setHeroVideoMilestones((prev) => {
          const newSet = new Set(prev);
          newSet.add(next);
          return newSet;
        });
        const eventName = `video_${next === 100 ? 'complete' : next}`;
        trackAction(eventName, 'video', 'hero_video');
        pushToDataLayer({ event: eventName });
      }
    }
  };


  const handleCTAClick = (ctaId: string, section: string) => {
    trackCTA(ctaId, section, 'button');
    pushToDataLayer({ event: 'cta_click', cta_id: ctaId, cta_section: section, page_path: '/landingpage2EN' });
  };

  const toggleFAQ = (index: number) => {
    setActiveFAQ(activeFAQ === index ? null : index);
    if (activeFAQ !== index) {
      trackAction('faq_open', 'interaction', `faq_${index}`);
      pushToDataLayer({ event: 'faq_open', question_id: index });
    }
  };

  // Book information for preview
  const booksInfo: Record<number, { title: string; desc: string }> = {
    1: {
      title: 'Book 1 – Learning to Learn',
      desc:
        'Master techniques to remember better and study with intention. Design your routine with simple goals and weekly tracking.',
    },
    2: {
      title: 'Book 2 – Learning to Think',
      desc:
        'Think with clarity and judgment: logic, evidence, and biases. Solve real problems step by step.',
    },
    3: {
      title: 'Book 3 – Learning to Debate',
      desc:
        'Argue with respect and strength. Learn to listen, build ideas, and debate with clear rules.',
    },
    4: {
      title: 'Book 4 – Learning to Feel',
      desc:
        'Recognize, name, and regulate emotions. Practices of calm and empathy to make better decisions.',
    },
    5: {
      title: 'Book 5 – Learning to Interact',
      desc:
        'Connect and cooperate better. Social skills, collaboration, and effective communication.',
    },
    6: {
      title: 'Book 6 – Learning to Generate Value',
      desc:
        'Turn ideas into value for others. Observe needs, design solutions, and measure impact.',
    },
    7: {
      title: 'Book 7 – Learning to Take Care of Yourself',
      desc:
        'Take care of your body and daily energy. Sleep, movement, nutrition, and measurable habits.',
    },
    8: {
      title: 'Book 8 – Learning to Finance Yourself',
      desc:
        'Your first project. From an idea to a micro-enterprise: plan, test, and first sales.',
    },
  };

  return (
    <div id="app-root" className="relative flex min-h-[100svh] flex-col w-full overflow-x-clip" style={{ backgroundColor: 'transparent', fontFamily: '"Fredoka", sans-serif' }}>
      {/* Hero Section with Video and Navigation */}
      <section className="relative min-h-[100svh] flex flex-col overflow-hidden">
        {/* Video as background */}
        <video
          ref={heroVideoRef}
          className="absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster="/images/landingpage/video-poster.jpg"
          onPlay={handleVideoPlay}
          onTimeUpdate={handleHeroVideoProgress}
          aria-label="Video presentation of the 8 books"
        >
          <source src="/images/reservas/videoLandingReservar.mp4" type="video/mp4" />
          Your browser does not support the video element.
        </video>
        
        {/* Subtle overlay for readability (optional) */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30 z-10 pointer-events-none"></div>

        {/* Decorative curve over the video */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ zIndex: 15 }}>
          <svg
            className="block w-full"
            style={{ height: '150px', display: 'block' }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 150"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 Q360,100 720,50 T1440,80 Q1080,20 720,60 T0,40 L0,150 L1440,150 Z"
              fill="rgb(254 252 232)"
            />
          </svg>
        </div>

        {/* Header over the video */}
        <header className="relative z-20 bg-transparent" style={{ backgroundColor: 'transparent !important' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-3 sm:py-4">
              <div className="flex items-center">
                <img
                  src="/images/princonser kids logo.png"
                  alt="Princonser Kids Logo"
                  className="h-10 sm:h-12 md:h-14 w-auto"
                  loading="lazy"
                />
              </div>
              
              {/* Social Media Icons */}
              <div className="flex items-center space-x-3 sm:space-x-4">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/princonserkids"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCTAClick('social_facebook', 'header')}
                  className="text-white hover:text-blue-400 transition-colors duration-300"
                  aria-label="Princonser Kids Facebook"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/princonserkids_esp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCTAClick('social_instagram', 'header')}
                  className="text-white hover:text-pink-300 transition-colors duration-300"
                  aria-label="Princonser Kids Instagram"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                
                {/* TikTok */}
                <a
                  href="https://www.tiktok.com/@princonserkids"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCTAClick('social_tiktok', 'header')}
                  className="text-white hover:text-gray-300 transition-colors duration-300"
                  aria-label="Princonser Kids TikTok"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                
                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/princonserkids"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCTAClick('social_linkedin', 'header')}
                  className="text-white hover:text-blue-300 transition-colors duration-300"
                  aria-label="Princonser Kids LinkedIn"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </header>
      </section>

      <main className="flex-1 relative bg-gradient-to-b from-gray-50 to-white">

        {/* Social Validation */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200">
              <p className="text-center text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 font-semibold uppercase tracking-wide">
                International academic and philosophical backing
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
                <div className="flex items-center space-x-3">
                  <img
                    src="/images/ForoFilosofia.png"
                    alt="World Philosophical Forum"
                    className="h-12 sm:h-16 w-auto opacity-90"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-gray-900 font-semibold text-sm">World Philosophical Forum</div>
                    <div className="text-gray-600 text-xs">International Institution</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <img
                    src="/images/PrinconserUniversity.png"
                    alt="Princonser University"
                    className="h-12 sm:h-16 w-auto opacity-90"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-gray-900 font-semibold text-sm">Princonser University</div>
                    <div className="text-gray-600 text-xs">Certified University</div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs sm:text-sm text-gray-600 mt-4">Advisor: Fidel G. Vivanco</p>
            </div>
          </div>
        </section>


        {/* Kickstarter */}
        <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-yellow-50 via-yellow-100 to-orange-50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-10 left-10 w-20 h-20 sm:w-24 sm:h-24 bg-purple-200 rounded-full opacity-20 blur-xl"></div>
          <div className="absolute top-20 right-20 w-16 h-16 sm:w-20 sm:h-20 bg-pink-200 rounded-full opacity-20 blur-xl"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 sm:w-28 sm:h-28 bg-blue-200 rounded-full opacity-20 blur-xl"></div>
          
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Header with prominent rocket */}
            <div className="text-center mb-10 sm:mb-12">
              <div className="inline-block mb-4 sm:mb-6">
                <div className="text-6xl sm:text-7xl md:text-8xl animate-bounce">🚀</div>
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-4 sm:mb-6">
                <span className="block mb-2">Coming soon</span>
                <span className="block mb-2">on</span>
                <span className="block text-cyan-500 drop-shadow-lg">KICKSTARTER</span>
              </h2>
            <div className="mt-2 sm:mt-3">
              <p className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                Take advantage of the{' '}
                <span className="text-red-600 text-3xl sm:text-4xl md:text-5xl font-black">50% discount</span>
              </p>
              <p className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-wide">Limited spots</p>
            </div>
              <p className="text-xl sm:text-2xl text-gray-700 max-w-3xl mx-auto font-medium mb-6">
                <span className="font-bold">Reserve your spot</span> to be part of the first Immersive Comprehensive Training Experience, based on Wisdom and <span className="font-bold">unlock exclusive rewards</span>
              </p>
            </div>

            {/* Email form */}
            <div className="max-w-md mx-auto">
              {kickstarterSuccess ? (
                <div className="bg-white rounded-2xl p-6 text-center shadow-lg border border-green-200">
                  <div className="text-4xl mb-2">🎉</div>
                  <p className="text-xl font-extrabold text-gray-900">Welcome to the waitlist!</p>
                  <p className="text-gray-700 mt-1">We'll notify you when it's available on Kickstarter.</p>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!kickstarterEmail.trim() || !kickstarterPrivacy) return;
                    setKickstarterLoading(true);
                    setKickstarterError(null);
                    try {
                      handleCTAClick('kickstarter_form_submit', 'kickstarter_section');
                      pushToDataLayer({
                        event: 'generate_lead',
                        form_type: 'kickstarter_waitlist',
                        email: kickstarterEmail,
                      });
                      await waitlistService.joinKickstarter(kickstarterEmail.trim());
                      setKickstarterSuccess(true);
                      setKickstarterEmail('');
                      setKickstarterPrivacy(false);
                    } catch (err: any) {
                      setKickstarterError('There was a problem. Please try again in a few seconds.');
                    } finally {
                      setKickstarterLoading(false);
                    }
                  }}
                  className="space-y-4"
                >
                  {/* Email field */}
                  <input
                    type="email"
                    value={kickstarterEmail}
                    onChange={(e) => setKickstarterEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base"
                    required
                  />

                  {/* Privacy checkbox */}
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="kickstarter-privacy"
                      checked={kickstarterPrivacy}
                      onChange={(e) => setKickstarterPrivacy(e.target.checked)}
                      className="mt-1 w-5 h-5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 flex-shrink-0"
                      required
                    />
                    <label
                      htmlFor="kickstarter-privacy"
                      className="text-sm sm:text-base text-gray-700 cursor-pointer"
                    >
                      I have read and accept the{' '}
                      <a href="/privacidad" className="text-cyan-600 underline hover:text-cyan-700" target="_blank" rel="noopener noreferrer">
                        privacy policy
                      </a>
                    </label>
                  </div>

                  {kickstarterError && (
                    <p className="text-sm text-red-600">{kickstarterError}</p>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={kickstarterLoading}
                    onClick={() => handleCTAClick('kickstarter_form_click', 'kickstarter_section')}
                    className="w-full inline-flex items-center justify-center px-6 py-4 sm:px-8 sm:py-5 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-4 focus:ring-yellow-300 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {kickstarterLoading ? 'Sending…' : "I'm interested!"}
                  </button>
                </form>
              )}
            </div>

            
          </div>
        </section>

        

        {/* 8 books preview */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            backgroundImage: "url('/images/reservas/FondoNuestrosLibros.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] pointer-events-none"></div>
            <div className="relative">
            <div className="text-center mb-8 sm:mb-12">
              <StackedTitle
                lines={[
                  { text: 'What do the Princonser Kids', className: 'text-5xl sm:text-6xl md:text-7xl' },
                  { text: 'characters teach us?', highlight: true },
                ]}
                sub="They provide comprehensive training in 4 areas:"
                align="center"
                color="text-cyan-500"
              />
              
              {/* Pillar cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
                {/* Mental Health - Purple */}
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-xl sm:text-2xl font-bold text-white text-center">
                    Mental Health
                  </h3>
                </div>
                
                {/* Social Health - Orange */}
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-xl sm:text-2xl font-bold text-white text-center">
                    Social Health
                  </h3>
                </div>
                
                {/* Physical Health - Green */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-xl sm:text-2xl font-bold text-white text-center">
                    Physical Health
                  </h3>
                </div>
                
                {/* Financial Health - Gold */}
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-xl sm:text-2xl font-bold text-white text-center">
                    Financial Health
                  </h3>
                </div>
              </div>
            </div>

            {/* Grid of 8 books - 2 rows of 4 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((bookNum) => (
                <div key={bookNum} className="flex flex-col">
                  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-200 h-full flex flex-col">
                    <img
                      src={`/images/portadas/portadalibro${bookNum}.png`}
                      alt={`Book ${bookNum}`}
                      className="w-full h-auto mb-4 rounded-lg"
                      loading="lazy"
                    />
                    <h3 className="font-bold text-lg sm:text-xl mb-2 text-gray-800">{booksInfo[bookNum].title}</h3>
                    <p className="text-sm text-gray-600 mb-3 flex-grow">{booksInfo[bookNum].desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            
            </div>
          </div>
        </section>

        

        {/* Debate */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            backgroundImage: "url('/images/reservas/FondoAppDebate.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-white/50 backdrop-blur-[1.5px] pointer-events-none"></div>
            <div className="relative">
            <div className="text-center mb-8 sm:mb-12">
              <StackedTitle
                lines={[
                  { text: 'Practice makes perfect' },
                  { text: 'Debate Game', highlight: true },
                ]}
                sub="Through the Debate Game we measure wisdom in an international ranking"
                align="center"
                color="text-yellow-500"
              />
            </div>
            
            <div className="grid grid-cols-1 gap-8 sm:gap-12">
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200">
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <video
                    src="/images/reservas/ArenaDebate.mp4"
                    className="w-full h-auto"
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label="Horizontal demo video of DebateApp"
                  >
                    Your browser does not support the video element.
                  </video>
                </div>
              </div>
            </div>
            
            </div>
          </div>
        </section>

        

        {/* Complete Methodology */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            backgroundImage: "url('/images/landingpage/FondoNuestra.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1.5px] pointer-events-none"></div>
            <div className="relative text-center mb-8 sm:mb-12">
              <StackedTitle
                lines={[
                  { text: 'What makes us different?', className: 'text-5xl sm:text-6xl md:text-7xl' },
                  { text: 'The most complete methodology', highlight: true },
                ]}
                sub="We have the most complete combination of educational methodologies in the market"
                align="center"
                color="text-purple-600"
              />
            </div>

            {/* Methodology mosaic */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Challenges */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/Desafios.png')" }}
                  aria-label="Challenges"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Challenges</span>
                </div>
              </div>

              {/* Writing */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/Escritura.png')" }}
                  aria-label="Writing"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Writing</span>
                </div>
              </div>

              {/* Scientific Experiments */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/ExperimentosCientificos.png')" }}
                  aria-label="Scientific Experiments"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Scientific Experiments</span>
                </div>
              </div>

              {/* Social Experiments */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/ExperimentosSociales.png')" }}
                  aria-label="Social Experiments"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Social Experiments</span>
                </div>
              </div>

              {/* Multimedia Training */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/FormacionMultimedia.png')" }}
                  aria-label="Multimedia Training"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Multimedia Training</span>
                </div>
              </div>

              {/* Games */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/Juegos.png')" }}
                  aria-label="Games"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Games</span>
                </div>
              </div>

              {/* Reading */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/Lectura.png')" }}
                  aria-label="Reading"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Reading</span>
                </div>
              </div>

              {/* Metacognition */}
              <div className="relative h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: "url('/images/reservas/Metacognicion.png')" }}
                  aria-label="Metacognition"
                  role="img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                  <span className="inline-block text-white font-bold text-lg sm:text-xl drop-shadow">Metacognition</span>
                </div>
              </div>
            </div>
            
          </div>
        </section>

        

        {/* Two vertical halves (left and right) */}
        <section className="grid grid-cols-1 lg:grid-cols-2 min-h-[700px] sm:min-h-[800px] lg:min-h-[900px]">
          {/* Part A - Left */}
          <div
            className="relative min-h-[600px] sm:min-h-[700px]"
            style={{
              backgroundImage: "url('/images/reservas/lemonadeBoy.png')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
            <div className="relative z-10 h-full flex items-start pt-12 sm:pt-16 lg:pt-20 px-6 sm:px-8 lg:px-12">
              <div className="max-w-xl">
                <h3 className="font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white mb-4 sm:mb-6 drop-shadow-lg">We awaken their purpose</h3>
                <p className="text-white/95 text-lg sm:text-xl lg:text-2xl leading-relaxed drop-shadow-md">
                  From interest to a fundable project or vocation. We transform curiosity into measurable action.
                </p>
              </div>
            </div>
          </div>

          {/* Part B - Right */}
          <div
            className="relative min-h-[600px] sm:min-h-[700px]"
            style={{
              backgroundImage: "url('/images/reservas/recitalGirl.png')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-l from-black/60 via-black/40 to-black/20"></div>
            <div className="relative z-10 h-full flex items-end justify-end pb-12 sm:pb-16 lg:pb-20 px-6 sm:px-8 lg:px-12">
              <div className="max-w-xl text-right">
                <h3 className="font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white mb-4 sm:mb-6 drop-shadow-lg">We take them to action</h3>
                <p className="text-white/95 text-lg sm:text-xl lg:text-2xl leading-relaxed drop-shadow-md">
                  Each child discovers their unique interests through stories, experiments, and guided reflections.
                </p>
              </div>
            </div>
          </div>

          
        </section>

        

        {/* FAQ */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white relative">
          {/* Refund seal overlapped in corner, between FAQ and final CTA */}
          <img
            src="/images/reservas/SelloDevolucion.png"
            alt="Seal: If you don't like it, we'll refund your money"
            className="hidden sm:block absolute right-6 md:right-10 lg:right-16 -bottom-10 md:-bottom-12 lg:-bottom-14 z-40 w-28 md:w-48 lg:w-56 drop-shadow-xl pointer-events-none"
            loading="lazy"
          />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
                Frequently Asked Questions
              </h2>
            </div>
            
            <div className="space-y-4">
              {[
                {
                  q: 'How much time does it require per week?',
                  a: 'Very little adult time. Children can work independently with clear guides. You only need 15-20 minutes per week to review their progress and celebrate achievements.',
                },
                {
                  q: 'From what age?',
                  a: 'The system is designed for children ages 7-12 (primary education), when cognitive development according to Piaget is most critical.',
                },
                {
                  q: 'Privacy and children\'s data',
                  a: 'We collect minimal data and comply with GDPR/Kids. The ranking is opt-in and parents have full control over shared data.',
                },
                {
                  q: 'What if I don\'t like it?',
                  a: 'We offer a satisfaction guarantee and you can download sample #1 before committing.',
                },
              ].map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg cursor-pointer border border-gray-200 hover:bg-gray-100 transition-colors"
                  onClick={() => toggleFAQ(index)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && toggleFAQ(index)}
                  aria-expanded={activeFAQ === index}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg sm:text-xl text-gray-800">{faq.q}</h3>
                    <span className="text-2xl text-gray-600">{activeFAQ === index ? '−' : '+'}</span>
                  </div>
                  {activeFAQ === index && <p className="mt-4 text-gray-600">{faq.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Final CTA */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-yellow-500/90 to-orange-500/90 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8">
              Reserve now and receive sample #1
            </h2>
            <CTAButton
              ctaName="Reserve now"
              section="Final CTA"
              onClick={() => {
                handleCTAClick('final_reserve_now', 'final_section');
                navigate('/reservar/formulario');
              }}
            >
              Reserve now our first experience
            </CTAButton>
            
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400 text-sm sm:text-base">
              &copy; 2024 Princonser Kids. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm sm:text-base mt-2">
              Forming wise children with purpose.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReservarPageEN;

